import type { PlanAssignmentDto, PlanReasonDto } from '@room-mates/shared';
import type { ReactElement } from 'react';
import { formatWeight } from '../../shared/format.js';
import { useTranslation } from '../../shared/i18n/useLanguage.js';

const MARK: Record<PlanReasonDto['kind'], string> = {
  satisfied: '✓',
  violated: '✕',
  neutral: '·',
};

/**
 * The reasons behind one placement, strongest first.
 *
 * The weight is shown because it is the honest answer to "why did this beat the
 * alternative?" — the plan is a sum, and hiding the arithmetic would make the
 * explanation decorative.
 */
export function PlanReasons({ reasons }: { reasons: readonly PlanReasonDto[] }): ReactElement {
  const ordered = [...reasons].sort((a, b) => Math.abs(b.weight) - Math.abs(a.weight));

  return (
    <ul className="data-list" style={{ gap: 0 }}>
      {ordered.map((reason, index) => (
        <li key={`${reason.code}-${index}`} className={`reason reason--${reason.kind}`}>
          <span className="reason__mark" aria-hidden="true">
            {MARK[reason.kind]}
          </span>
          <span>{reason.message}</span>
          <span className="reason__weight">{formatWeight(reason.weight)}</span>
        </li>
      ))}
    </ul>
  );
}

export function PlanAlternatives({
  assignment,
}: {
  assignment: PlanAssignmentDto;
}): ReactElement | null {
  const t = useTranslation();
  if (assignment.alternatives.length === 0) {
    return null;
  }

  return (
    <div className="panel stack stack--tight">
      <strong style={{ fontSize: '0.88rem' }}>{t.planReasons.equallyGoodAlternatives}</strong>
      <ul className="data-list" style={{ gap: 'var(--space-1)' }}>
        {assignment.alternatives.map((alternative, index) => (
          <li key={`${alternative.bedId}-${index}`} className="reason reason--neutral">
            <span className="reason__mark" aria-hidden="true">
              {alternative.kind === 'swap' ? '⇄' : '→'}
            </span>
            <span>{alternative.note}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
